/**
 * Firestore metadata for pharmacy order update action fields at
 * `/configurations/pharmacy_order_updates/metadata/pa_requests`.
 */

import type { CmmInputUniqueValueItem } from "../pages/configurations/pages/validationFunnel/pages/conditionList/cmmInputUniqueValuesTypes";
import type {
  FieldDefinition,
  OperatorDefinition,
  QueryBuilderConfig,
  QueryBuilderOption,
} from "./queryBuilder";
import {
  getBooleanOperators,
  getNumericOperators,
  getStringOperators,
  getTimestampOperators,
  getUnknownDatatypeOperators,
  normalizeQueryBuilderOptions,
  withSelectValuesOnStringEqualsOperators,
} from "./queryBuilder";

/** Single field option stored on the metadata document. */
export interface PaRequestsMetadataOption {
  value: string;
  label?: string;
}

/** Column entry when metadata mirrors condition-list / unique-values shape. */
export interface PaRequestsMetadataColumn {
  column_name: string;
  field_name?: string;
  display_name?: string;
  data_type?: string;
  show_on_condition_list?: boolean;
  top_values?: (CmmInputUniqueValueItem | string | number | boolean)[];
}

/**
 * Controls whether condition field lists honor `show_on_condition_list`.
 * POC assignment passes `filterByShowOnConditionList: false` so hidden columns appear there only.
 */
export interface ConditionFieldListOptions {
  /** When true (default), columns with `show_on_condition_list === false` are omitted. */
  filterByShowOnConditionList?: boolean;
}

/** Document at `/configurations/pharmacy_order_updates/metadata/pa_requests`. */
export interface PaRequestsMetadataDoc {
  id?: string;
  /** Primary list of action field dropdown options. */
  options?: PaRequestsMetadataOption[];
  /** Optional column definitions (used when `options` is absent). */
  columns?: PaRequestsMetadataColumn[];
}

/**
 * Converts a top_value entry to a query-builder option.
 *
 * @param t - Top value item or primitive from Firestore
 * @returns Value/label pair for selects
 */
function topValueToOption(
  t: CmmInputUniqueValueItem | string | number | boolean,
): QueryBuilderOption {
  const isItemShape = t != null && typeof t === "object" && "value" in t;
  const value = isItemShape ? (t as CmmInputUniqueValueItem).value : t;
  const count = isItemShape ? (t as CmmInputUniqueValueItem).count : undefined;
  const valueStr = String(value ?? "").slice(0, 80);
  const suffix = String(value ?? "").length > 80 ? "…" : "";
  const countPart = count !== undefined ? ` (${count})` : "";
  return {
    value: String(value ?? ""),
    label: `${valueStr}${suffix}${countPart}`,
  };
}

/**
 * Maps the pa_requests metadata document to action field dropdown options.
 *
 * @param doc - Fetched metadata document, if any
 * @returns Options for the action Field SearchableSelect
 */
export function mapPaRequestsMetadataToFieldOptions(
  doc: PaRequestsMetadataDoc | null | undefined,
): QueryBuilderOption[] {
  if (!doc) return [];

  if (doc.options?.length) {
    return normalizeQueryBuilderOptions(
      doc.options.map((o) => ({
        value: o.value,
        label: o.label ?? o.value,
      })),
    );
  }

  if (doc.columns?.length) {
    return normalizeQueryBuilderOptions(
      doc.columns.map((col) => {
        const rawLabel =
          col.display_name ??
          col.field_name ??
          col.column_name?.replace(/_/g, " ") ??
          "";
        return {
          value: col.column_name,
          label: rawLabel.charAt(0).toUpperCase() + rawLabel.slice(1),
        };
      }),
    );
  }

  return [];
}

/**
 * Maps a metadata column to a QueryBuilder field definition.
 *
 * @param col - Column entry from the pa_requests metadata document
 * @returns Field definition for condition rows and value inputs
 */
function buildFieldFromMetadataColumn(
  col: PaRequestsMetadataColumn,
): FieldDefinition {
  const topValues = col.top_values ?? [];
  const valueOptionsFromTopValues =
    topValues.length > 0
      ? topValues.slice(0, 100).map(topValueToOption)
      : undefined;

  const dt = (col.data_type ?? "").toUpperCase();
  let operators: OperatorDefinition[];

  if (dt === "STRING") {
    operators = withSelectValuesOnStringEqualsOperators(
      getStringOperators(valueOptionsFromTopValues ?? undefined),
      valueOptionsFromTopValues,
    );
  } else if (
    dt === "INTEGER" ||
    dt === "FLOAT" ||
    dt === "FLOAT64" ||
    dt === "FLOAT32" ||
    dt === "INT64" ||
    dt === "INT32"
  ) {
    operators = getNumericOperators();
  } else if (dt === "BOOLEAN") {
    operators = getBooleanOperators();
  } else if (dt === "TIMESTAMP" || dt === "DATE" || dt === "DATETIME") {
    operators = getTimestampOperators(valueOptionsFromTopValues ?? undefined);
  } else {
    operators = withSelectValuesOnStringEqualsOperators(
      getUnknownDatatypeOperators(valueOptionsFromTopValues ?? undefined),
      valueOptionsFromTopValues,
    );
  }

  const rawLabel =
    col.display_name ?? col.field_name ?? col.column_name.replace(/_/g, " ");
  return {
    value: col.column_name,
    label: rawLabel.charAt(0).toUpperCase() + rawLabel.slice(1),
    operators,
    dataType: col.data_type,
    valueOptions: valueOptionsFromTopValues,
  };
}

/**
 * Builds a full QueryBuilderConfig from pa_requests metadata for condition field
 * dropdowns (first column in each condition row).
 *
 * @param doc - Fetched metadata document, if any
 * @param options - When `filterByShowOnConditionList` is false, all columns are included
 * @returns Config with fields derived from `columns` or `options`
 */
export function buildConditionConfigFromPaRequestsMetadata(
  doc: PaRequestsMetadataDoc | null | undefined,
  options?: ConditionFieldListOptions,
): QueryBuilderConfig {
  if (!doc) return { fields: [] };

  const filterByShowOnConditionList =
    options?.filterByShowOnConditionList !== false;

  if (doc.columns?.length) {
    const columns = filterByShowOnConditionList
      ? doc.columns.filter((col) => col.show_on_condition_list !== false)
      : doc.columns;
    return { fields: columns.map(buildFieldFromMetadataColumn) };
  }

  if (doc.options?.length) {
    return {
      fields: doc.options.map((o) => ({
        value: o.value,
        label: o.label ?? o.value,
        operators: getStringOperators(),
      })),
    };
  }

  return { fields: [] };
}

/**
 * Merges metadata-driven fields into a page condition config when metadata is present.
 *
 * @param pageConfig - Page-specific condition config (static + optional CMM fields)
 * @param metadata - Fetched pa_requests metadata document
 * @param options - Passed to {@link buildConditionConfigFromPaRequestsMetadata}
 * @returns Merged config, or null when metadata provides no fields
 */
export function mergePaRequestsMetadataIntoConditionConfig(
  pageConfig: QueryBuilderConfig,
  metadata: PaRequestsMetadataDoc | null | undefined,
  options?: ConditionFieldListOptions,
): QueryBuilderConfig | null {
  const metadataConfig = buildConditionConfigFromPaRequestsMetadata(
    metadata,
    options,
  );
  if (metadataConfig.fields.length === 0) return null;

  const metadataByField = new Map(
    metadataConfig.fields.map((f) => [f.value, f]),
  );
  const pageFieldNames = new Set(pageConfig.fields.map((f) => f.value));

  const mergedPageFields = pageConfig.fields.map(
    (f) => metadataByField.get(f.value) ?? f,
  );
  const metadataOnlyFields = metadataConfig.fields.filter(
    (f) => !pageFieldNames.has(f.value),
  );

  return { fields: [...mergedPageFields, ...metadataOnlyFields] };
}

/**
 * Builds a minimal QueryBuilderConfig from metadata columns for action value
 * top_values (used by ActionSetValueInput).
 *
 * @param doc - Fetched metadata document
 * @returns Config with fields and valueOptions from column top_values
 */
export function buildValueConfigFromPaRequestsMetadata(
  doc: PaRequestsMetadataDoc | null | undefined,
): QueryBuilderConfig {
  if (!doc?.columns?.length) {
    return { fields: [] };
  }

  return {
    fields: doc.columns.map((col) => {
      const rawLabel =
        col.display_name ??
        col.field_name ??
        col.column_name.replace(/_/g, " ");
      const topValues = col.top_values ?? [];
      const valueOptionsFromTopValues =
        topValues.length > 0
          ? topValues.slice(0, 100).map(topValueToOption)
          : undefined;

      return {
        value: col.column_name,
        label: rawLabel.charAt(0).toUpperCase() + rawLabel.slice(1),
        operators: [],
        dataType: col.data_type,
        valueOptions: valueOptionsFromTopValues,
      };
    }),
  };
}
